import React, { useState } from 'react';
import { Mail, MapPin, Loader2, CheckCircle } from 'lucide-react';
import * as Icons from 'lucide-react';
import contactData from '../data/contact.json';
import PageHero from '../components/PageHero';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });
      
      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', subject: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const iconMap = { Mail, MapPin };

  return (
    <div className="relative">
      <PageHero 
        title={contactData.pageTitle}
        subtitle={contactData.pageSubtitle}
      />

      <section className="section-padding pt-0">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div data-testid="contact-form-section">
              <div className="glass-panel p-8">
                <form onSubmit={handleSubmit} className="space-y-6">
                  {contactData.form.fields.map((field) => {
                    if (field.type === 'textarea') {
                      return (
                        <div key={field.name}>
                          <label className="block text-sm font-medium text-white mb-2">
                            {field.label}
                            {field.required && <span className="text-pink-500 ml-1">*</span>}
                          </label>
                          <textarea
                            name={field.name}
                            value={formData[field.name]}
                            onChange={handleChange}
                            placeholder={field.placeholder}
                            required={field.required}
                            rows={5}
                            className="input-dark w-full resize-none"
                            data-testid={`input-${field.name}`}
                          />
                        </div>
                      );
                    }

                    if (field.type === 'select') {
                      return (
                        <div key={field.name}>
                          <label className="block text-sm font-medium text-white mb-2">
                            {field.label}
                            {field.required && <span className="text-pink-500 ml-1">*</span>}
                          </label>
                          <select
                            name={field.name}
                            value={formData[field.name]}
                            onChange={handleChange}
                            required={field.required}
                            className="input-dark w-full"
                            data-testid={`input-${field.name}`}
                          >
                            <option value="">Select an option</option>
                            {field.options.map((option) => (
                              <option key={option} value={option}>
                                {option}
                              </option>
                            ))}
                          </select>
                        </div>
                      );
                    }

                    return (
                      <div key={field.name}>
                        <label className="block text-sm font-medium text-white mb-2">
                          {field.label}
                          {field.required && <span className="text-pink-500 ml-1">*</span>}
                        </label>
                        <input
                          type={field.type}
                          name={field.name}
                          value={formData[field.name]}
                          onChange={handleChange}
                          placeholder={field.placeholder}
                          required={field.required}
                          className="input-dark w-full"
                          data-testid={`input-${field.name}`}
                        />
                      </div>
                    );
                  })}

                  <button
                    type="submit"
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="submit-contact-btn"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 size={20} className="animate-spin" />
                        Sending...
                      </>
                    ) : (
                      contactData.form.submitText
                    )}
                  </button>
                  
                  {submitStatus === 'success' && (
                    <div className="flex items-center gap-2 p-4 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400">
                      <CheckCircle size={20} />
                      <span>Thank you! Your message has been sent to hello@romind.tech</span>
                    </div>
                  )}
                  
                  {submitStatus === 'error' && (
                    <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400">
                      Something went wrong. Please try again or email us directly at hello@romind.tech
                    </div>
                  )}
                </form>
              </div>
            </div>

            {/* Alternative Contact */}
            <div data-testid="alternative-contact">
              <h2 className="text-2xl font-bold text-white mb-6">
                {contactData.alternativeContact.title}
              </h2>

              <div className="space-y-6">
                {contactData.alternativeContact.items.map((item, index) => {
                  const IconComponent = iconMap[item.icon];
                  return (
                    <div 
                      key={index}
                      className="glass-panel p-6 flex items-start gap-4"
                    >
                      <div className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center text-violet-400 shrink-0">
                        {IconComponent && <IconComponent size={24} />}
                      </div>
                      <div>
                        <h3 className="text-white font-medium mb-1">{item.label}</h3>
                        <p className="text-slate-400">{item.value}</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Additional info */}
              <div className="mt-8 glass-panel p-6">
                <h3 className="text-lg font-semibold text-white mb-3">Response Time</h3>
                <p className="text-slate-400">
                  We typically respond within 24-48 hours. For urgent matters, please indicate so in your message subject.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding cosmic-bg">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
            Join the early access list
          </h2>
          <p className="text-slate-400 mb-8">
            Be among the first to experience ROMIND when we launch.
          </p>
          <a 
            href="/#early-access" 
            className="btn-primary"
            data-testid="contact-cta"
          >
            Get Early Access
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;
